// src/contexts/UserContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { loadAndMergeProgress, forceSyncNow } from '../services/gameProgressSync';

const USER_STORAGE_KEY = 'evcs_user';

const UserContext = createContext(null);

// Load persisted user from localStorage (survives page refresh on Safari iOS)
const loadPersistedUser = () => {
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const UserProvider = ({ children }) => {
  console.log('[BOOT] UserProvider: mounting');
  const [user, setUser] = useState(loadPersistedUser); // restore from localStorage on mount
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);

  // Keep localStorage in sync whenever user changes
  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    } catch {
      // localStorage unavailable — silent fail
    }
  }, [user]);

  // Function to handle user login and progress sync
  // Set skipSync=true when restoring from localStorage (user was already synced last session)
  const handleUserLogin = async (userData, gameContext, { skipSync = false } = {}) => {
    setUser(userData);

    if (skipSync) {
      console.log('ℹ️ User restored from local storage — skipping S3 sync');
      return;
    }

    if (!gameContext) {
      console.warn('⚠️ GameContext not available for sync');
      return;
    }

    // Load and merge progress from S3
    setIsLoadingProgress(true);
    try {
      console.log('🔄 User logged in, syncing progress...');
      
      const localProgress = gameContext.progress;
      const mergedProgress = await loadAndMergeProgress(
        userData.sub,
        userData.email,
        localProgress
      );

      // Update GameContext with merged progress
      if (mergedProgress._hasNewData) {
        console.log('✅ Progress merged with new data from S3');
        gameContext.setProgress(mergedProgress);
        
        // Show notification to user (optional)
        // Could dispatch a custom event here for UI to show toast
        window.dispatchEvent(new CustomEvent('progress-synced', {
          detail: { hasNewData: true }
        }));
      } else {
        console.log('ℹ️ Progress up to date');
      }

    } catch (error) {
      console.error('❌ Failed to sync progress on login:', error);
    } finally {
      setIsLoadingProgress(false);
    }
  };

  // Function to handle user logout
  const handleUserLogout = async (gameContext) => {
    if (user && gameContext) {
      // Force immediate sync before logout
      console.log('💾 Syncing progress before logout...');
      await forceSyncNow(user.sub, user.email, gameContext.progress);
    }
    
    setUser(null);
    console.log('👋 User logged out');
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser,
      isLoadingProgress,
      handleUserLogin,
      handleUserLogout,
    }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook for easy access
export const useUser = () => useContext(UserContext);
